<template>
  <div class="tenant-selector" :class="{ readonly: readonly }">
    <div class="label">
      {{ $t('navBar.tenant') }}
    </div>
    <LocaleSelect
      :model-value="contextStore.currentTenant"
      :items="tenants"
      item-title="name"
      item-value="key"
      :readonly="readonly"
      @update:model-value="handleTenantChange"
    />
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { useContextStore } from '@/stores/context';
import { isEmbedded } from '@/webshell-embed/embedded';

const contextStore = useContextStore();

// Get tenants from store (they're loaded with user context)
const tenants = computed(() => contextStore.getAvailableTenants || []);

// Automatically set readonly based on number of available tenants
const readonly = computed(() => tenants.value.length <= 1);

const handleTenantChange = tenantKey => {
  // Embedded, the tenant is part of the shell URL - ADR-004 - https://github.com/owg-digital/rtls-one-retail/tree/main/frontend/shell/docs/adrs/004-tenant-in-url-path.md
  // and changing tenants triggers a full reload of the browser page
  if (isEmbedded()) throw new Error('the shell owns the tenant while embedded');
  if (!tenantKey) return;

  // Update store state (context store is saved to local storage)
  contextStore.setCurrentTenant(tenantKey);

  // Redirect to home page (current page might be for an entity that is specific to previous tenant)
  const newUrl = new URL('/', window.location.origin);
  newUrl.searchParams.set('tenant', tenantKey);

  // force a page reload to ensure all data is fetched with new tenant
  window.location.href = newUrl.toString();
};
</script>

<style lang="scss" scoped>
.label {
  font-size: 1.2rem;
  font-weight: normal;
  min-width: 5rem;
}

.tenant-selector {
  display: flex;
  align-items: center;

  &.readonly {
    opacity: 0.8;

    :deep(.v-field__append-inner) {
      display: none; // Hide dropdown arrow for readonly
    }
  }
}
</style>
